import {twMerge} from '../../../common/twMerge';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {LabelBadge16Type} from './LabelBadge16Type.enum';
import {LabelBadge18Type} from './LabelBadge18Type.enum';
import {LabelBadge28Type} from './LabelBadge28Type.enum';
import {LabelBadgeProps} from './LabelBadgeProps.interface';
import {LabelBadgeSizeType} from './LabelBadgeSizeType.enum';

export function LabelBadge({
  sizeType,
  type,
  badge,
  label,
  labelClassName,
  isStatic,
  isRest,
  className,
  ...props
}: LabelBadgeProps) {
  if (sizeType === LabelBadgeSizeType.Size16) {
    const isBadge = type === LabelBadge16Type.badge;
    const isSolid = type === LabelBadge16Type.solid;
    const isLine = type === LabelBadge16Type.line;

    const validTypes = [
      LabelBadge16Type.red,
      LabelBadge16Type.black,
      LabelBadge16Type.yellow,
      LabelBadge16Type.violet,
      LabelBadge16Type.WUF,
    ];

    if (validTypes.includes(type)) {
      return (
        <div
          className={twMerge(
            'inline-flex h-16pxr items-center justify-center rounded-5pxr shrink-0',
            !(type === LabelBadge16Type.badge) && 'px-3pxr',
            BACKGROUND_COLOR_16_TYPE_MAP[type],
            className
          )}
          {...props}
        >
          {type === LabelBadge16Type.WUF ? (
            <div className="flex">
              <Icon
                type={IconType.ContentHomeTime2Static}
                className="w-16pxr h-16pxr"
                disableActiveEffect
              />
              <div className="flex h-16pxr items-center rounded-r-5pxr bg-static-mono-white pl-2pxr pr-4pxr">
                <Text
                  className={twMerge(
                    'font-x-small2-bold',
                    TEXT_COLOR_16_TYPE_MAP[type]
                  )}
                >
                  {label}
                </Text>
              </div>
            </div>
          ) : (
            <Text
              className={twMerge(
                'font-x-small2-bold',
                TEXT_COLOR_16_TYPE_MAP[type],
                labelClassName
              )}
            >
              {label}
            </Text>
          )}
        </div>
      );
    }
    return (
      <div
        className={twMerge(
          'inline-flex h-16pxr items-center justify-center rounded-5pxr shrink-0',
          !isBadge && 'px-4pxr',
          isSolid && 'bg-theme-solid-100 text-theme-solid-0',
          isSolid &&
            !isStatic &&
            isRest &&
            'bg-theme-solid-100 text-static-point-red',
          isLine && 'border-1 bg-transparent',
          isLine &&
            isStatic &&
            !isRest &&
            'border-[rgb(var(--colors-static-mono-black)_/_10%)] text-static-mono-black',
          isLine &&
            !isStatic &&
            !isRest &&
            'border-theme-transparent-20 text-theme-solid-100',
          isLine &&
            !isStatic &&
            isRest &&
            'border-theme-transparent-20 text-static-point-red',
          className
        )}
        {...props}
      >
        {type === LabelBadge16Type.badge && badge ? (
          <Icon type={badge} disableActiveEffect />
        ) : (
          <Text className={twMerge('font-x-small2-bold', labelClassName)}>
            {label}
          </Text>
        )}
      </div>
    );
  } else if (sizeType === LabelBadgeSizeType.Size18) {
    return (
      <div
        className={twMerge(
          'inline-flex h-18pxr items-center justify-center rounded-5pxr shrink-0',
          !(type === LabelBadge18Type.badge) && 'px-4pxr',
          BACKGROUND_COLOR_18_TYPE_MAP[type],
          className
        )}
        {...props}
      >
        {type === LabelBadge18Type.WUF ? (
          <div className="flex">
            <Icon type={IconType.ContentHomeTime2Static} disableActiveEffect />
            <div className="flex h-18pxr items-center rounded-r-5pxr bg-static-mono-white px-3pxr">
              <Text className="font-x-small1-bold text-static-mono-black">
                {label}
              </Text>
            </div>
          </div>
        ) : type === LabelBadge18Type.badge ? (
          badge && <Icon type={badge} disableActiveEffect />
        ) : (
          <Text
            className={twMerge(
              'font-x-small1-bold',
              TEXT_COLOR_18_TYPE_MAP[type],
              labelClassName
            )}
          >
            {label}
          </Text>
        )}
      </div>
    );
  } else if (sizeType === LabelBadgeSizeType.Size28) {
    return (
      <div
        className={twMerge(
          'inline-flex h-28pxr cursor-pointer items-center justify-center rounded-100pxr px-12pxr shrink-0',
          BACKGROUND_COLOR_28_TYPE_MAP[type],
          type === LabelBadge28Type.line &&
            'border-1 border-theme-transparent-20',
          className
        )}
        {...props}
      >
        <Text
          className={twMerge(
            'font-small2-bold',
            TEXT_COLOR_28_TYPE_MAP[type],
            labelClassName
          )}
        >
          {label}
        </Text>
      </div>
    );
  }
  return null;
}

/** 뱃지 배경/텍스트 색상 매핑 */
const BACKGROUND_COLOR_16_TYPE_MAP: {[key in LabelBadge16Type]?: string} = {
  [LabelBadge16Type.red]: 'bg-static-point-red',
  [LabelBadge16Type.black]: 'bg-static-mono-black',
  [LabelBadge16Type.yellow]: 'bg-static-point-yellow',
  [LabelBadge16Type.violet]: 'bg-static-point-violet',
};
const TEXT_COLOR_16_TYPE_MAP: {[key in LabelBadge16Type]?: string} = {
  [LabelBadge16Type.red]: 'text-static-mono-white',
  [LabelBadge16Type.black]: 'text-static-mono-white',
  [LabelBadge16Type.yellow]: 'text-static-mono-black',
  [LabelBadge16Type.violet]: 'text-static-mono-white',
  [LabelBadge16Type.WUF]: 'text-static-mono-black',
};
const BACKGROUND_COLOR_18_TYPE_MAP: {[key in LabelBadge18Type]: string} = {
  [LabelBadge18Type.red]: 'bg-static-point-red',
  [LabelBadge18Type.blue]: 'bg-static-point-blue',
  [LabelBadge18Type.yellow]: 'bg-static-point-yellow',
  [LabelBadge18Type.gray]: 'bg-static-mono-40',
  [LabelBadge18Type.WUF]: '',
  [LabelBadge18Type.badge]: '',
  [LabelBadge18Type.el70]: 'bg-theme-solid-100',
};
const TEXT_COLOR_18_TYPE_MAP: {[key in LabelBadge18Type]: string} = {
  [LabelBadge18Type.red]: 'text-static-mono-white',
  [LabelBadge18Type.blue]: 'text-static-mono-white',
  [LabelBadge18Type.yellow]: 'text-static-mono-black',
  [LabelBadge18Type.gray]: 'text-static-mono-white',
  [LabelBadge18Type.WUF]: '',
  [LabelBadge18Type.badge]: '',
  [LabelBadge18Type.el70]: 'text-theme-solid-0',
};
const BACKGROUND_COLOR_28_TYPE_MAP: {[key in LabelBadge28Type]: string} = {
  [LabelBadge28Type.ticket]: 'bg-static-point-yellow',
  [LabelBadge28Type.refund]: 'bg-static-point-red',
  [LabelBadge28Type.solid]: 'bg-theme-solid-100',
  [LabelBadge28Type.line]: 'bg-transparent',
};
const TEXT_COLOR_28_TYPE_MAP: {[key in LabelBadge28Type]: string} = {
  [LabelBadge28Type.ticket]: 'text-static-mono-black',
  [LabelBadge28Type.refund]: 'text-static-mono-white',
  [LabelBadge28Type.solid]: 'text-theme-solid-0',
  [LabelBadge28Type.line]: 'text-theme-solid-100',
};
